import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Container, Flex, Pagination } from '@legion-ui/core';
import Button from '@/components/Button';
import SearchBar from '@/components/SearchBar';
import RecipientsTable from '@/components/RecipientsTable';
import AddRecipientsModal from '@/components/AddRecipientsModal';
import { RecipientType, mockRecipients } from '@/mocks/recipientsMockData';
import { useUpdateEmailBlast, useSendEmailBlast } from '@/hooks/useEmailBlastQuery';

// Helper to get storage key for recipients
const getRecipientsStorageKey = (emailBlastId: string) => `emailblast_recipients_${emailBlastId}`;

const AddRecipients: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as {
    emailBlastId?: string;
    subject?: string;
    content?: string;
    existingRecipients?: string[];
  } | null;
  
  const updateEmailBlast = useUpdateEmailBlast();
  const sendEmailBlast = useSendEmailBlast();
  
  // Initialize with existing recipients from state or empty array
  const [selectedRecipients, setSelectedRecipients] = useState<RecipientType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper function to update navigation state (for back navigation consistency)
  const updateNavigationState = useCallback((newRecipients: RecipientType[]) => {
    if (!state?.emailBlastId) return;
    
    // Save to sessionStorage for refresh persistence
    const storageKey = getRecipientsStorageKey(state.emailBlastId);
    sessionStorage.setItem(storageKey, JSON.stringify(newRecipients));
    
    // Update location state for back navigation
    navigate(location.pathname, {
      replace: true,
      state: {
        ...state,
        existingRecipients: newRecipients.map(r => ({
          username: r.username,
          email: r.email,
          fullname: r.fullName,
        })),
      },
    });
  }, [state, navigate, location.pathname]);
  
  // Load existing recipients from sessionStorage first, then fallback to state
  useEffect(() => {
    if (!state?.emailBlastId) return;
    
    const storageKey = getRecipientsStorageKey(state.emailBlastId);
    const storedRecipients = sessionStorage.getItem(storageKey);
    
    if (storedRecipients) {
      // Load from sessionStorage (persists across refresh)
      try {
        const parsed = JSON.parse(storedRecipients);
        setSelectedRecipients(parsed);
        return;
      } catch (e) {
        console.error('Error parsing stored recipients:', e);
      }
    }
    
    // Fallback to state (from navigation)
    if (state?.existingRecipients && state.existingRecipients.length > 0) {
      // Map existing recipients (objects) to RecipientType objects
      const mappedRecipients = state.existingRecipients.map((recipient: any) => {
        return {
          username: recipient.username || '',
          email: recipient.email || '',
          fullName: recipient.fullname || '',
        };
      });
      setSelectedRecipients(mappedRecipients);
      
      // Also save to sessionStorage
      sessionStorage.setItem(storageKey, JSON.stringify(mappedRecipients));
    }
  }, [state]);

  // Cleanup sessionStorage when leaving the page completely (not just refresh)
  useEffect(() => {
    return () => {
      // Note: This won't run on refresh, only on actual unmount (navigation away)
      // We keep the data in sessionStorage for refresh scenarios
    };
  }, []);

  const handleAddRecipients = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAddRecipientsFromModal = (finalRecipientsFromModal: RecipientType[]) => {
    let newRecipients: RecipientType[] = [];
    
    setSelectedRecipients((prev) => {
      // Create a map of existing recipients by username
      const existingMap = new Map(prev.map((r) => [r.username, r]));
      
      // Create a set to track which usernames are in the incoming data
      const incomingUsernames = new Set(finalRecipientsFromModal.map((r) => r.username));
      
      // Keep existing recipients that are NOT in the incoming list
      const keptRecipients = prev.filter((r) => !incomingUsernames.has(r.username));
      
      // For incoming recipients, prefer complete data from existing if available
      const mergedNewRecipients = finalRecipientsFromModal.map((r) => {
        const existing = existingMap.get(r.username);
        // If recipient already existed with complete data, keep the complete data
        if (existing && (existing.email || existing.fullName)) {
          return existing;
        }
        // Otherwise use the new data from modal (which should be complete now)
        return r;
      });
      
      // Combine kept recipients with merged new recipients
      newRecipients = [...keptRecipients, ...mergedNewRecipients];
      return newRecipients;
    });

    // Update navigation state for persistence
    setTimeout(() => updateNavigationState(newRecipients), 0);

    setIsModalOpen(false);
  };

  const handleSaveRecipientsToAPI = async (usernames: string[]) => {
    if (!state?.emailBlastId) {
      throw new Error('Email blast ID is required');
    }
    
    await updateEmailBlast.mutateAsync({
      id: state.emailBlastId,
      data: {
        subject: state.subject || '',
        content: state.content || '',
        recipients: usernames,
      }
    });
  };

  const handleRemoveRecipient = async (username: string) => {
    if (!state?.emailBlastId) {
      return;
    }
    
    // Update local state first for instant UI feedback
    const updatedRecipients = selectedRecipients.filter((recipient) => recipient.username !== username);
    setSelectedRecipients(updatedRecipients);
    
    // Update navigation state for persistence
    updateNavigationState(updatedRecipients);
    
    // Then save to API
    try {
      const recipientUsernames = updatedRecipients.map(r => r.username);
      await updateEmailBlast.mutateAsync({
        id: state.emailBlastId,
        data: {
          subject: state.subject || '',
          content: state.content || '',
          recipients: recipientUsernames,
        }
      });
    } catch (error: any) {
      console.error('Error removing recipient:', error);
      // Revert on error
      const revertedRecipients = [...updatedRecipients, { username, email: null, fullName: null }];
      setSelectedRecipients(revertedRecipients);
      updateNavigationState(revertedRecipients);
    }
  };

  const handleRemoveAllRecipients = async () => {
    if (!state?.emailBlastId || selectedRecipients.length === 0) {
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove all recipients?')) {
      return;
    }
    
    // Save current recipients for rollback on error
    const previousRecipients = [...selectedRecipients];
    
    // Update local state first for instant UI feedback
    setSelectedRecipients([]);
    
    // Update navigation state for persistence (empty array)
    updateNavigationState([]);
    
    // Then save to API
    try {
      await updateEmailBlast.mutateAsync({
        id: state.emailBlastId,
        data: {
          subject: state.subject || '',
          content: state.content || '',
          recipients: [],
        }
      });
    } catch (error: any) {
      console.error('Error removing all recipients:', error);
      // Revert on error
      setSelectedRecipients(previousRecipients);
      updateNavigationState(previousRecipients);
    }
  };

  // Helper function to clear session storage
  const clearRecipientsStorage = useCallback(() => {
    if (state?.emailBlastId) {
      const storageKey = getRecipientsStorageKey(state.emailBlastId);
      sessionStorage.removeItem(storageKey);
    }
  }, [state?.emailBlastId]);

  const handleSaveAsDraft = async () => {
    if (!state?.emailBlastId) {
      return;
    }
    
    try {
      // Extract usernames from selected recipients
      const recipientUsernames = selectedRecipients.map(r => r.username);
      
      await updateEmailBlast.mutateAsync({
        id: state.emailBlastId,
        data: {
          subject: state.subject || '',
          content: state.content || '',
          recipients: recipientUsernames,
        }
      });
      
      // Clear session storage before navigating away
      clearRecipientsStorage();
      
      // Navigate back after successful save
      navigate('/email-blast');
    } catch (error: any) {
      console.error('Error saving draft:', error);
    }
  };

  const handleSend = async () => {
    if (!state?.emailBlastId || selectedRecipients.length === 0) {
      return;
    }
    
    if (window.confirm('Are you sure you want to send this email blast? This action cannot be undone.')) {
      try {
        // First, update the recipients
        const recipientUsernames = selectedRecipients.map(r => r.username);
        
        await updateEmailBlast.mutateAsync({
          id: state.emailBlastId,
          data: {
            subject: state.subject || '',
            content: state.content || '',
            recipients: recipientUsernames,
          }
        });
        
        // Then, send the email
        await sendEmailBlast.mutateAsync(state.emailBlastId);
        
        // Clear session storage before navigating away
        clearRecipientsStorage();
        
        alert('Email blast is being processed. Emails will be sent in the background.');
        navigate('/email-blast');
      } catch (error: any) {
        console.error('Error sending email blast:', error);
        alert(error?.response?.data?.message || 'Failed to send email blast');
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handlePageSizeChange = (dataSizeOption: number) => {
    setPageSize(dataSizeOption);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Filter recipients based on search query
  const filteredRecipients = selectedRecipients.filter((recipient) => {
    if (searchQuery === '') return true;
    return (
      recipient.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (recipient.email && recipient.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (recipient.fullName && recipient.fullName.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  // Pagination
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRecipients = filteredRecipients.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredRecipients.length / pageSize);

  console.log('Debug pagination:', {
    totalRecipients: selectedRecipients.length,
    filteredCount: filteredRecipients.length,
    pageSize,
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    paginatedCount: paginatedRecipients.length,
  });

  // Determine if we're in edit mode based on emailBlastId
  const isEditMode = !!state?.emailBlastId;

  return (
    <Container>
      <Box className="bg-white min-h-screen">
        {/* Header */}
        <Box className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Bulk Email' : 'Create Bulk Email'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">Manage Sent email notification</p>
        </Box>

        {/* Content */}
        <Box className="px-6 py-6">
          <Box className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Add Recipients</h2>
            </div>

            {/* Search and Add Button */}
            <div className="flex items-center justify-between mb-6 gap-4">
              <div className="flex-1 max-w-md">
                <SearchBar
                  label="Search by username"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search"
                  required={false}
                />
              </div>
              <div className="pt-7 flex gap-2">
                {selectedRecipients.length > 0 && (
                  <Button
                    variant="outline"
                    color="error"
                    size="md"
                    onClick={handleRemoveAllRecipients}
                    className="px-6"
                    disabled={updateEmailBlast.isLoading}
                  >
                    Remove All
                  </Button>
                )}
                <Button
                  variant="solid"
                  color="primary"
                  size="md"
                  onClick={handleAddRecipients}
                  className="px-6"
                >
                  <span className="mr-2">+</span> Add Recipients
                </Button>
              </div>
            </div>

            {/* Recipients Table */}
            <RecipientsTable
              recipients={paginatedRecipients}
              onRemove={handleRemoveRecipient}
              startIndex={startIndex}
            />

            {/* Pagination */}
            {selectedRecipients.length > 0 && (
              <Box mt="1rem">
                <Flex alignX="flex-end">
                  <Pagination
                    key={`pagination-${currentPage}-${pageSize}`}
                    currentPage={currentPage}
                    totalPages={filteredRecipients.length}
                    onClickPage={handlePageChange}
                    variant="rounded"
                    onClickOption={handlePageSizeChange}
                    options={[
                      pageSize.toString(),
                      ...['10', '20', '30', '40', '50'].filter(opt => opt !== pageSize.toString())
                    ]}
                  />
                </Flex>
              </Box>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 mt-8">
              <Button
                variant="outline"
                color="primary"
                size="md"
                onClick={() => {
                  clearRecipientsStorage();
                  navigate(`/email-blast/edit/${state?.emailBlastId}`);
                }}
                className="px-8"
              >
                Back
              </Button>
              <Button
                variant="outline"
                color="primary"
                size="md"
                onClick={handleSaveAsDraft}
                className="px-8"
              >
                Save as Draft
              </Button>
              <Button
                variant="solid"
                color="primary"
                size="md"
                onClick={handleSend}
                className="px-8 disabled:!opacity-100 disabled:!bg-[#0649CF] disabled:!text-white disabled:cursor-not-allowed"
                disabled={selectedRecipients.length === 0 || sendEmailBlast.isLoading || updateEmailBlast.isLoading}
              >
                {sendEmailBlast.isLoading ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </Box>
        </Box>
      </Box>

      {/* Add Recipients Modal */}
      <AddRecipientsModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onAdd={handleAddRecipientsFromModal}
        availableRecipients={mockRecipients}
        alreadySelectedIds={selectedRecipients.map((r) => r.username)}
        onSaveRecipients={handleSaveRecipientsToAPI}
      />
    </Container>
  );
};

export default AddRecipients;
